import { useState, useEffect, useRef, Fragment } from "react";
import styled from "styled-components/macro";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  BoxContent,
  BoxHeader,
  BoxNavLeft,
  BoxNavButton,
  BoxNavRight,
} from "./Box";
import { BORDER_RADIUS } from "../vars";
import { ChevronLeft, Heart, HeartFilled, FullScreen } from "./Icons";
import { useSkipioApi, touchSkipioApi } from "../api";
import usePlan from "../hooks/use-plan";
import { useToast } from "../hooks/use-toast";
import Button from "./Button";
import SidePanel, { SidePanelTrigger } from "./SidePanel";
import Stack from "./Stack";
import useTitle from "../hooks/use-title";
import useMoment from "../hooks/use-moment";
import useRootHistory, { History } from "../hooks/use-root-history";
import HtmlFormat from "./HtmlFormat";
import { useSkipioQuery } from "../hooks/use-query";

interface Node {
  type: string;
  text: string;
  children: Node[];
  src: string;
  url: string;
  id: string;
}

interface Props {
  articleId: string;
  hide: () => void;
  isSidePanel: boolean;
  history: History;
}

function PostViewerContent({ articleId, hide, isSidePanel, history }: Props) {
  const Moment = useMoment();
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const article = useSkipioQuery({ queryKey: ["articles", articleId] }).data;
  useTitle(article.title);
  const [isImporting, setIsImporting] = useState(false);
  let isFavorited = false;
  if (article.articleInteraction && article.articleInteraction.isFavorited)
    isFavorited = true;

  const plan = usePlan();
  const contentRef = useRef<HTMLDivElement>(null);
  const date = Moment(article.publishAt).fromNow();

  async function markFavorite() {
    await skipioApi.articleInteractions.post(
      {},
      {
        data: {
          articleInteraction: {
            isViewed: true,
            articleId,
            isFavorited: !isFavorited,
          },
        },
      }
    );
    await touchSkipioApi("articles", "articleInteractions");
    await queryClient.invalidateQueries({ queryKey: ["articles"] });
    await queryClient.invalidateQueries({ queryKey: ["articleInteractions"] });
  }

  const attributedTo = article.agency
    ? article.agency.name
    : `${article.author.firstName} ${article.author.lastName}`;

  useEffect(() => {
    skipioApi.articleInteractions
      .post({}, { data: { articleInteraction: { isViewed: true, articleId } } })
      .then(() => {
        return () => {
          touchSkipioApi("articles", "articleInteractions");
          queryClient.invalidateQueries({ queryKey: ["articles"] });
          queryClient.invalidateQueries({ queryKey: ["articleInteractions"] });
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const token = article.campaignPackageImportToken;

  const showToast = useToast();

  async function importCampaign(token: string): Promise<void> {
    if (plan.isFree) {
      history.push("/app/plan");
      return;
    }

    if (isImporting) return;
    setIsImporting(true);

    const {
      data: { campaignIds: ids },
    } = await skipioApi.campaignPackageImport
      .post({ importToken: token })
      .catch((err: Error) => {
        setIsImporting(false);
        showToast("Could not import campaign", { success: false });
        throw err;
      });

    setIsImporting(false);

    if (ids.length) {
      history.push(`/app/campaigns/${ids[0]}`);

      // this keeps the spinner running while the page refreshes
      return new Promise(() => {});
    } else {
      showToast(
        "The campaign import was successful, but nothing was imported...",
        { success: false }
      );
    }
  }

  useEffect(() => {
    if (!contentRef.current) return;

    const element = contentRef.current;

    element.addEventListener("click", () => {}, false);

    function delegate(selector: string, event: string, fn: (e: Event) => void) {
      const handler = (e: Event) => {
        for (
          let target = e.target as HTMLElement | Document | null;
          target && target !== document;
          target = target.parentNode as HTMLElement | Document | null
        ) {
          if (target instanceof Element && target.matches(selector)) {
            fn.call(target, e);
            break;
          }
        }
      };

      element.addEventListener(event, handler, false);
    }

    delegate("a", "click", (e) => {
      const a = e.target as HTMLAnchorElement;
      const rootRegex = /https:\/\/app.skipio.com\/dashboard\/post\//;
      if (rootRegex.test(a.href)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        history.push(a.href.replace(rootRegex, `/app/resource-center/post/`));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  function renderItem(node: Node, index: number) {
    if (!node.type) {
      if (node.text) return <Fragment key={index}>{node.text}</Fragment>;
      return null;
    }

    const children = (node.children || []).map(renderItem);

    switch (node.type) {
      case "paragraph":
        return <p key={index}>{children}</p>;
      case "block-quote":
        return <blockquote key={index}>{children}</blockquote>;
      case "heading-one":
        return <h2 key={index}>{children}</h2>;
      case "heading-two":
        return <h3 key={index}>{children}</h3>;
      case "bulleted-list":
        return <ul key={index}>{children}</ul>;
      case "numbered-list":
        return <ol key={index}>{children}</ol>;
      case "list-item":
        return <li key={index}>{children}</li>;
      case "image":
        return (
          <div key={index}>
            <img src={node.src} alt={article.title} />
          </div>
        );
      case "link":
        return (
          <a href={node.url} key={index}>
            {children}
          </a>
        );
      case "youtube":
        return (
          <div key={index}>
            <YoutubeContainer contentEditable={false}>
              <iframe
                title="YouTube Video"
                src={`https://www.youtube.com/embed/${node.id}`}
                frameBorder="0"
                width="100%"
                height="100%"
              />
            </YoutubeContainer>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <>
      <BoxHeader>
        <BoxNavLeft>
          <BoxNavButton
            onClick={() => {
              hide();
            }}
          >
            <ChevronLeft /> Go Back
          </BoxNavButton>
        </BoxNavLeft>
        <BoxNavRight>
          {isSidePanel && (
            <BoxNavButton
              onClick={() => {
                hide();
                history.push(`/app/resource-center/post/${articleId}`);
              }}
            >
              <FullScreen />
              &nbsp;Full Screen
            </BoxNavButton>
          )}
          <BoxNavButton onClick={markFavorite}>
            {isFavorited ? (
              <HeartFilled style={{ color: "red" }}></HeartFilled>
            ) : (
              <Heart style={{ color: "red" }}></Heart>
            )}
            &nbsp;Favorite
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent data-ignore="true">
        <Post>
          <Stack>
            <Banner style={{ backgroundImage: `url(${article.bannerUrl})` }} />
            <Title>{article.title}</Title>
            <Subheader>
              <InfoContainer>
                <User>
                  <Name>{attributedTo}</Name>
                  <DatePublished>{date}</DatePublished>
                </User>
              </InfoContainer>

              {Boolean(token) && (
                <BoxNavRight>
                  <Button
                    variant="primary"
                    onClick={() => importCampaign(token)}
                  >
                    import the campaign
                  </Button>
                </BoxNavRight>
              )}
            </Subheader>

            <HtmlFormat ref={contentRef}>
              {article.content.map(renderItem)}
            </HtmlFormat>
            {Boolean(token) && (
              <div style={{ textAlign: "center" }}>
                <Button variant="primary" onClick={() => importCampaign(token)}>
                  import the campaign
                </Button>
              </div>
            )}
          </Stack>
        </Post>
      </BoxContent>
    </>
  );
}

function PostViewer({
  match: {
    params: { articleId },
  },
  ...props
}: {
  match: { params: { articleId: string } };
}) {
  const history = useRootHistory();
  return (
    <Box>
      <PostViewerContent
        {...props}
        isSidePanel={false}
        hide={() => {
          history.goBack();
        }}
        articleId={articleId}
        history={history}
      />
    </Box>
  );
}

export function PostViewerSidePanel({
  trigger,
  articleId,
}: {
  trigger: SidePanelTrigger;
  articleId: string;
}) {
  const history = useRootHistory();
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => (
        <PostViewerContent
          history={history}
          isSidePanel={true}
          hide={() => {
            hide();
          }}
          articleId={articleId}
        />
      )}
    />
  );
}

const Name = styled.div`
  text-transform: uppercase;
  font-weight: 700;
`;

const InfoContainer = styled.div`
  display: flex;
`;

const DatePublished = styled.div``;

const User = styled.div`
  display: flex;
  flex-direction: column;
  color: rgb(103, 97, 99);
  justify-content: center;
`;

const Subheader = styled.div`
  display: flex;
  align-items: center;
`;

const Banner = styled.div`
  border-radius: ${BORDER_RADIUS};
  background: #0080ff;
  height: 30ch;
  margin-bottom: 40px;
  background-size: cover;
  background-position: center center;
`;

const Post = styled.div`
  text-align: left;
  margin: auto;
  margin-bottom: 40px;
  max-width: 75ch;
  font-size: 16px;
  line-height: 1.8em;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 500;
  color: #444;
`;

const YoutubeContainer = styled.div`
  padding-top: 56%;
  position: relative;
  & > * {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 7px;
  }
`;

export default PostViewer;
